import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ClientApi } from "@/api/client.api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { ClientTableSkeleton } from "@/components/users/ClientTableSkeleton";
import { getFormattedDateTime } from "@/utils";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Trash2, Edit } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { LoaderButton } from "@/components/ui/loader-button";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { EditClientSheet } from "@/components/clients/EditClientSheet";
import { useDebounce } from "@/hooks/use-debounce";

type ClientSearchType = "id" | "clientName" | "clientCode";

const SEARCH_TYPE_LABELS: Record<ClientSearchType, string> = {
  id: "Client ID",
  clientName: "Client Name",
  clientCode: "Client Code",
};

const Clients = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [searchType, setSearchType] = useState<ClientSearchType>("clientName");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalClients, setTotalClients] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [clientToEdit, setClientToEdit] = useState(null);

  const buildSearchPayload = (query: string, type: ClientSearchType) => {
    const trimmed = query.trim();
    return {
      id: type === "id" && trimmed ? trimmed : null,
      clientName: type === "clientName" && trimmed ? trimmed : null,
      clientCode: type === "clientCode" && trimmed ? trimmed : null,
    };
  };

  const fetchClients = () => {
    setLoading(true);
    ClientApi.getClientsFromDB({
      ...buildSearchPayload(debouncedSearchQuery, searchType),
      pageNumber: currentPage - 1,
      pageSize: rowsPerPage,
      sortOrder: "desc",
      sortBy: "id",
    })
      .then((res: any) => {
        const data = res.data;
        setTotalClients(data.totalNumberOfClients || 0);
        setClients(
          (data.clientVOs || []).map((c: any) => ({
            id: c.id?.toString() ?? "",
            createdAt: c.createDate ?? "",
            name: c.clientName ?? "",
            contact: c.clientContactNumber ?? "",
            email: c.clientEmail ?? "",
            code: c.clientCode ?? "",
            address: c.clientAddress ?? "",
            country: c.clientCountry ?? "",
            currency: c.currencyVO?.iso3CurrencyCode ?? "",
            analytics: "",
          }))
        );
      })
      .catch(() => {
        setClients([]);
        setTotalClients(0);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchClients();
  }, [currentPage, rowsPerPage, debouncedSearchQuery, searchType]);

  const totalPages = Math.ceil(totalClients / rowsPerPage);

  const resetFilters = () => {
    setSearchType("clientName");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleDeleteClient = (id: string) => {
    setClientToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteClient = async () => {
    if (!clientToDelete) return;
    try {
      setIsDeleting(true);
      await ClientApi.deleteClient(clientToDelete);
      toast.success("Client deleted successfully");
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      fetchClients();
    } catch (error) {
      toast.error("Failed to delete client");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditClient = (client) => {
    setClientToEdit(client);
    setIsEditSheetOpen(true);
  };

  const getClientCreatePath = () => {
    if (location.pathname.startsWith("/xpandpoint"))
      return "/xpandpoint/clients/create";
    if (location.pathname.startsWith("/rewardpoint"))
      return "/rewardpoint/clients/create";
    return "/clients/create";
  };

  const getClientDetailsPath = (clientId: string) => {
    if (location.pathname.startsWith("/xpandpoint"))
      return `/xpandpoint/clients/${clientId}`;
    if (location.pathname.startsWith("/rewardpoint"))
      return `/rewardpoint/clients/${clientId}`;
    return `/clients/${clientId}`;
  };

  return (
    <>
      <Card className="w-full border-0 shadow-sm card-modern">
        <CardHeader className="bg-white rounded-t-lg border-b border-gray-100 py-3 px-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="text-2xl font-semibold">
              Client Management
            </CardTitle>
            <Button
              onClick={() => navigate(getClientCreatePath())}
              className="flex items-center gap-2"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Client
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="mb-6 flex flex-col sm:flex-row gap-4 items-end sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
            <div className="flex-1 flex gap-2 items-end w-full">
              <div>
                <Select
                  value={searchType}
                  onValueChange={(value: ClientSearchType) => {
                    setSearchType(value);
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="id">Client ID</SelectItem>
                    <SelectItem value="clientName">Client Name</SelectItem>
                    <SelectItem value="clientCode">Client Code</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                  <Input
                    id="client-search"
                    placeholder={`Search by ${SEARCH_TYPE_LABELS[searchType].toLowerCase()}...`}
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2 px-3 py-2 text-sm"
              onClick={resetFilters}
              disabled={loading}
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
          </div>
          <div className="w-full overflow-x-auto">
            {loading ? (
              <ClientTableSkeleton />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Created Date</TableHead>
                    <TableHead>Client ID</TableHead>
                    <TableHead>Client Name</TableHead>
                    <TableHead>Client Contact</TableHead>
                    <TableHead>Client Code</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-center py-8 text-gray-500"
                      >
                        No clients found
                      </TableCell>
                    </TableRow>
                  ) : (
                    clients.map((client) => (
                      <TableRow key={client.id} className="h-16">
                        <TableCell>
                          {getFormattedDateTime(client.createdAt)}
                        </TableCell>
                        <TableCell>{client.id}</TableCell>
                        <TableCell>{client.name}</TableCell>
                        <TableCell>{client.contact}</TableCell>
                        <TableCell>{client.code}</TableCell>
                        <TableCell>
                          <div>{client.address}</div>
                          <div className="text-sm text-gray-500">
                            {client.country}
                          </div>
                        </TableCell>
                        <TableCell>{client.currency}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal size={16} />
                                <span className="sr-only">Open menu</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleEditClient(client)}
                                className="flex items-center cursor-pointer"
                              >
                                <Edit size={16} className="mr-2" /> Edit Client
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDeleteClient(client.id)}
                                className="flex items-center text-red-600 cursor-pointer hover:text-red-600 focus:text-red-600"
                              >
                                <Trash2 size={16} className="mr-2" /> Delete
                                Client
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
          {!loading && totalClients > 0 && (
            <div className="mt-4 w-full">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">Rows per page:</span>
                  <Select
                    value={rowsPerPage.toString()}
                    onValueChange={(value) => {
                      setRowsPerPage(Number(value));
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[80px]">
                      <SelectValue placeholder={rowsPerPage.toString()} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 flex justify-center">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <button
                          className={`flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground ${
                            currentPage === 1
                              ? "pointer-events-none opacity-50"
                              : ""
                          }`}
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          aria-label="First page"
                          type="button"
                        >
                          <ChevronsLeft className="h-4 w-4" />
                        </button>
                      </PaginationItem>
                      {currentPage > 1 ? (
                        <PaginationPrevious
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(1, prev - 1))
                          }
                          className="gap-0 px-0"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </PaginationPrevious>
                      ) : (
                        <PaginationPrevious
                          onClick={() => {}}
                          className="pointer-events-none opacity-50 gap-0 px-0"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </PaginationPrevious>
                      )}
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          const pageNumber =
                            currentPage <= 3 ? i + 1 : currentPage + i - 2;
                          if (pageNumber > totalPages) return null;
                          return (
                            <PaginationItem key={pageNumber}>
                              <PaginationLink
                                onClick={() => setCurrentPage(pageNumber)}
                                isActive={currentPage === pageNumber}
                              >
                                {pageNumber}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        }
                      )}
                      {currentPage < totalPages ? (
                        <PaginationNext
                          onClick={() =>
                            setCurrentPage((prev) =>
                              Math.min(totalPages, prev + 1)
                            )
                          }
                          className="gap-0 px-0"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </PaginationNext>
                      ) : (
                        <PaginationNext
                          onClick={() => {}}
                          className="pointer-events-none opacity-50 gap-0 px-0"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </PaginationNext>
                      )}
                      <PaginationItem>
                        <button
                          className={`flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground ${
                            currentPage === totalPages
                              ? "pointer-events-none opacity-50"
                              : ""
                          }`}
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                          aria-label="Last page"
                          type="button"
                        >
                          <ChevronsRight className="h-4 w-4" />
                        </button>
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
                <div className="w-[160px]" />
              </div>
            </div>
          )}
          <AlertDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Client</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this client? This action
                  cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <LoaderButton
                  loading={isDeleting}
                  variant="destructive"
                  onClick={confirmDeleteClient}
                >
                  Delete
                </LoaderButton>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
      <EditClientSheet
        open={isEditSheetOpen}
        onOpenChange={setIsEditSheetOpen}
        client={clientToEdit}
        onUpdate={() => {
          fetchClients();
        }}
      />
    </>
  );
};

export default Clients;
